import * as React from "react";
import { useEffect, useState } from "react";
import { AmorgRole, AmorgUserRole, ApplicationFeature, permissionGroupHelper, permissionHelper, PublicAmorgUser, UserSettings, UserToken } from '../../../amorg-common';
import { getActiveUser } from "../../../api/authClientApi";
import { getMaybeActiveUserOrRefresh } from "../../../api/serverApiHelpers";
import { getLastUserIntroEmailSent, getRoles, getUser, getUserRoles, sendUserIntroEmail, updateUserSettings } from '../../../api/userClientApi';
import { Card } from "../../../designSystem/Card";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Modal } from "../../../designSystem/Modal";
import { ToggletipTheOne } from "../../../designSystem/ToggletipTheOne";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { Checkbox } from "../../../designSystem/inputs/Checkbox";
import { HtmlEditor } from "../../../designSystem/inputs/HtmlEditor";
import { Small } from "../../../designSystem/typography/Body";
import { toDateTimeString } from '../../../utils/date';
import { EmailEditor } from "./EmailEditor";
import { AdminPasswordEditor, PasswordEditor } from "./PasswordEditor";
import { PermissionGroupEditor } from './PermissionGroupEditor';
import { UserRolePanel } from "./UserRolePanel";
import { UserDetailsEditor } from "./UserDetailsEditor";

type UserPanelProps = {
    activeUser: PublicAmorgUser;
    targetUserId: string;
    onSelectedUserUpdated: () => void;
};
type UserPanelState = {
    activeUserFeatures:ApplicationFeature[];
    siteId:string;
    targetUser?: PublicAmorgUser;
    roles: AmorgRole[];
    targetUserRoles: AmorgUserRole[];
    inEditUserRole?: AmorgUserRole | null;
};
export default function UserPanel ({ activeUser, targetUserId, onSelectedUserUpdated }: UserPanelProps): JSX.Element
{

    const [data, setData] = useState<UserPanelState>({
        activeUserFeatures: [],
        siteId: '',
        targetUser: undefined,
        roles: [],
        targetUserRoles: [],
        inEditUserRole: null,
    });

    const getData = async () =>
    {
        const maybeActiveTokenUser = await getMaybeActiveUserOrRefresh();
        const activeTokenUser = maybeActiveTokenUser.tokenUser as UserToken;
        const activeUserFeatures = activeTokenUser.features;
        const siteId = activeTokenUser.siteId;

        const targetUser = activeUser.id === targetUserId 
            ? await getActiveUser() 
            : await getUser(targetUserId);

        if(!targetUser)
            return;

        const roles = permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'member')
            ? await getRoles()
            : [];

        const targetUserRoles = 
                permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'member')
                ? await getUserRoles(targetUserId)
                : [];
        setData(
            {
                activeUserFeatures,
                siteId: siteId,
                targetUser: targetUser,
                roles,
                targetUserRoles: targetUserRoles,
                inEditUserRole: null
            }
        );
    };

    useEffect(() =>
    {
        getData();
    }, [activeUser.permissionGroup.id]);
    
    async function handleSelectedUserUpdated ()
    {
        getData();
        onSelectedUserUpdated();
    }

    async function handleSettingsChange (settings: UserSettings)
    {
        if(!data.targetUser)
            return;

        await updateUserSettings(data.targetUser.id, settings);
        await handleSelectedUserUpdated();
    }

    const canAdminEdit = data.targetUser
        ? permissionHelper.canAdminEditTargetGroupId(activeUser.permissionGroup.id, data.targetUser.permissionGroup.id, activeUser.id === data.targetUser.id)
        : false;

    return (

        <Flex justifyContent={"center"}>
            <Flex gap={32} flexDirection="column" width="75%" style={{ minWidth: '300px', marginBottom: '50px' }}>
                {data.targetUser &&
                    <>

                        <UserDetailsEditor
                            user={data.targetUser}
                            onUserUpdated={handleSelectedUserUpdated} />

                        <Flex gap={16} flexDirection="row">
                            <FlexItem width="50%">
                                <Card variant="outlined">
                                    <EmailEditor
                                        siteId={data.siteId}
                                        selectedUser={data.targetUser}
                                        isAdmin={canAdminEdit} />
                                </Card>
                            </FlexItem>
                            <FlexItem width="50%">
                                <Card variant="outlined">
                                    {canAdminEdit
                                        ? <AdminPasswordEditor selectedUser={data.targetUser} />
                                        : <PasswordEditor selectedUser={data.targetUser} />
                                    }
                                </Card>
                            </FlexItem>
                        </Flex>
                    </>
                }


                <Card variant="outlined">
                    {data.targetUser &&
                        <PermissionGroupEditor
                            activeUser={activeUser}
                            targetUser={data.targetUser}
                            onSelectedUserUpdated={handleSelectedUserUpdated} />
                    }
                </Card>


                {data?.activeUserFeatures.includes('Articles') &&
                    <Card variant="outlined">
                        <Flex>
                            <Checkbox
                                text="Lovservice kontaktperson"
                                checked={data.targetUser?.settings.articleContact ?? false}
                                value="articleContact"
                                onChange={(_, checked) => handleSettingsChange({ ...data.targetUser?.settings, articleContact: checked })}
                                disabled={!data.targetUser?.enabled}
                            />
                            <ToggletipTheOne horizontalAlignment="right" verticalAlignment="top">
                                <p>En lovservice kontaktperson modtager notifikationer om nye eller opdaterede artikler fra lovservice.</p>
                            </ToggletipTheOne>
                        </Flex>
                    </Card>
                }


                {data.targetUser && permissionGroupHelper.hasMinimumPermissionGroup(data.targetUser.permissionGroup.id, 'member') &&
                    <UserRolePanel userId={data.targetUser.id} disabled={!data.targetUser.enabled} />
                }

                {data.targetUser?.enabled && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroup.id, 'siteadmin') &&
                    <UserEmailModalWithButton siteId={data.siteId} selectedUser={data.targetUser} />
                }

            </Flex>
        </Flex>
    );
}


type UserEmailModalWithButtonProps = {
    siteId:string,
    selectedUser?: PublicAmorgUser
}
function UserEmailModalWithButton(props:UserEmailModalWithButtonProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [lastEmailSent, setLastEmailSent] = React.useState<Date | null>(null);
    const [message, setMessage] = React.useState<string>('');

    React.useEffect(()=>{
        refreshLastEmailSent();
    }, [props.selectedUser?.id]);

    async function refreshLastEmailSent()
    {
        if(props.selectedUser)
        {
            const emailSent = await getLastUserIntroEmailSent(props.selectedUser?.id);
            setLastEmailSent(emailSent);
        }
    }

    function handleClickOpen()
    {
        setMessage(
            `<p>Hej ${props.selectedUser?.firstname}</p>` +
            `<p><br /></p>` +
            `<p>Velkommen som bruger af arbejdsmiljøportalen Amorg.</p>` +
            `<p><br /></p>` +
            `<p>Før du kan logge ind, skal du oprette en adgangskode.</p>` +
            `<p>Det gør du ved at besøge dette link:</p>` +
            `<a href="${window.location.protocol}//${window.location.host}/request/setuppassword">${window.location.protocol}//${window.location.host}/request/setuppassword</a>` +
            `<p><br /></p>` +
            `<p>For at oprette en adgangskode skal du have adgang til følgende:</p>` + 
            `<p><br /></p>` +
            `<p>Den email du er oprettet med: ${props.selectedUser?.email || ''},</p>` +
            `<p>Din organisations id: ${props.siteId}</p>` +
            `<p><br /></p>` +
            `<p>Når du har oprettet en adgangskode, kan du logge ind og komme i gang med at bruge Amorg med det samme.</p>` +
            `<p><br /></p>` +
            `<p>Mange venlige hilsner fra din arbejdsmiljøorganisation</p>`
        );

        setShowModal(true);
    }

    async function handleClickSend()
    {
        if(!props.selectedUser)
            return;

        setLoading(true);
        await sendUserIntroEmail(props.selectedUser?.id || '', message);
        await refreshLastEmailSent();
        setLoading(false);

        setShowModal(false);
    }

    return (
        <div>
            <LoadingPageOverlay loading={loading} />

            <Flex flexDirection="column" gap={8}>
                <Button variant={lastEmailSent===null ? "filled" : "outlined"}
                    icon="interface_glyph_email"
                    text="Send velkomstemail"
                    onClick={handleClickOpen} />

                <Flex justifyContent="flex-end" alignItems="center" gap={8}>
                    {lastEmailSent &&
                        <Small>
                            Sidst sendt: {toDateTimeString(lastEmailSent)}
                        </Small>   
                    }
                    <ToggletipTheOne horizontalAlignment="right" verticalAlignment="top">
                        <p>Send en velkomstmail til brugeren med et link til at oprette en adgangskode.</p>
                        <p>Hvis brugeren allerede har modtaget en velkomstemail, kan du se hvornår den sidst blev sendt.</p>
                        <p>Du kan godt sende mere end en velkomstemail.</p>
                    </ToggletipTheOne>
                </Flex>
            </Flex>

            <Modal
                title="Send velkomstemail"
                showModal={showModal}
                onCancel={() => { setShowModal(false); }}
                content={

                    <Flex flexDirection="column" gap={16}>
                        <HtmlEditor
                            id="user-intro-email-message-editor"
                            value={message} 
                            onChange={(value) => setMessage(value)} 
                        />

                        <ButtonRow>
                            <Button 
                                variant="filled" 
                                text="Send email"
                                onClick={handleClickSend}
                            />
                        </ButtonRow>
                    </Flex>
                }
            />
        </div>
    );
}